import { messaging, vapidKey } from '../firebase-config';
import { getToken, onMessage } from 'firebase/messaging';

class NotificationService {
  constructor() {
    this.token = null;
    this.isInitialized = false;
  }

  // Initialize push notifications
  async initializeNotifications(userToken) {
    try {
      console.log('🔔 Initializing push notifications...');
      
      // Check if messaging is available (not supported on some mobile browsers)
      if (!messaging) {
        console.warn('⚠️ Firebase Messaging not available in this browser - skipping notification setup');
        return null;
      }
      
      // Check if Notification API is available
      if (!('Notification' in window)) {
        console.warn('⚠️ Notification API not supported in this browser');
        return null;
      }
      
      // Validate VAPID key
      if (!vapidKey || vapidKey === 'YOUR_VAPID_KEY_HERE' || vapidKey.length < 50) {
        console.error('❌ Invalid VAPID key. Please get the correct VAPID key from Firebase Console.');
        throw new Error('Invalid VAPID key. Please configure the VAPID key in firebase-config.js');
      }
      
      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('✅ Notification permission granted');
        
        // Get FCM token
        this.token = await getToken(messaging, {
          vapidKey: vapidKey
        });
        
        if (this.token) {
          console.log('🔑 FCM Token:', this.token);
          
          // Send token to backend
          await this.registerDeviceToken(userToken);
          
          // Listen for foreground messages
          onMessage(messaging, (payload) => {
            console.log('📨 Foreground message received:', payload);
            this.showNotification(payload.notification);
          });
          
          this.isInitialized = true;
          return this.token;
        } else {
          console.warn('⚠️ No FCM token received');
        }
      } else {
        console.warn('⚠️ Notification permission denied');
      }
    } catch (error) {
      console.error('❌ Error initializing notifications:', error);
    }
  }

  // Register device token with backend
  async registerDeviceToken(userToken) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/wallet/device-tokens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceToken: this.token,
          deviceType: 'web',
          deviceId: 'web-browser-' + Date.now(),
          appVersion: '1.0.0',
          userAgent: navigator.userAgent
        })
      });
      
      const result = await response.json();
      console.log('📱 Token registered with backend:', result);
      return result;
    } catch (error) {
      console.error('❌ Error registering token:', error);
    }
  }

  // Show notification in browser
  showNotification(notification) {
    if ('serviceWorker' in navigator && 'Notification' in window) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(notification.title, {
          body: notification.body,
          icon: '/icon-192x192.png',
          badge: '/badge-72x72.png',
          tag: 'wallet-notification',
          requireInteraction: true,
          actions: [
            {
              action: 'view',
              title: 'View'
            },
            {
              action: 'dismiss',
              title: 'Dismiss'
            }
          ]
        });
      });
    } else {
      // Fallback for browsers without service worker
      if (Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.body,
          icon: '/icon-192x192.png',
          tag: 'wallet-notification'
        });
      }
    }
  }

  // Test notification function
  async testNotification(userToken) {
    try {
      console.log('🧪 Sending test notification...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/test/merchant/send-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'system_alert',
          title: 'Web Test Notification',
          message: 'Testing web push notifications from admin panel!',
          data: {
            timestamp: new Date().toISOString(),
            source: 'admin-panel'
          }
        })
      });
      
      const result = await response.json();
      console.log('🧪 Test notification result:', result);
      
      // Check if notification was actually sent via FCM
      if (result.success && result.data && result.data.status === 'sent') {
        console.log('✅ Notification sent successfully via FCM');
        console.log('📊 Check Firebase Console → Messaging → Messages for delivery stats');
        console.log('⏰ Note: Firebase metrics may take 5-10 minutes to update');
        console.log('🔔 Notification ID:', result.data.id);
        console.log('📱 User ID:', result.data.user_id);
        
        // Check if browser notification appeared
        setTimeout(() => {
          console.log('🔍 Check if you received a browser notification popup');
          console.log('💡 If no popup appeared, check:');
          console.log('   - Browser notification settings');
          console.log('   - Do Not Disturb mode');
          console.log('   - Browser tab focus');
        }, 2000);
      } else {
        console.warn('⚠️ Notification may not have been sent via FCM');
        console.log('Response structure:', result);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Test notification failed:', error);
    }
  }

  // Get current token
  getToken() {
    return this.token;
  }

  // Check if notifications are initialized
  isNotificationsInitialized() {
    return this.isInitialized;
  }

  // Check notification permission
  getPermissionStatus() {
    return Notification.permission;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
